import { HALLIDAY_DOMAIN } from "@shared/constants";
import { Address } from "@shared/types";
import { isMobile } from "@shared/utils/browserUtils";

import {
    AppMode,
    PaymentFlowType,
    PaymentsWidgetSDKParams,
    SDKOverrides,
    WindowType,
    type CustomFunderRole,
    type GetBalances,
    type GetFee,
    type SDKAdditions,
    type SendFunds,
    type SendTransaction,
    type SignTypedData,
    type SolSendTransaction,
    type WalletMeta,
    type WalletVm,
    type OwnerMeta,
    type FunderRole,
} from "../types";
import { logError } from "../utils";
import { CUSTOM_FUNDER_ADDRESS_PREFIX, SDK_VERSION } from "../constants";

export type TaggedFunderSender =
    | { walletType: "EVM"; sendTransaction: SendTransaction }
    | { walletType: "SOL"; sendTransaction: SolSendTransaction }
    | { walletType: "CUSTOM"; getBalances: GetBalances; sendFunds: SendFunds; getFee?: GetFee | undefined };
export type FunderRegistry = Map<string, TaggedFunderSender>;

/** A funder role plus the flow it was registered under. Array position in this list is the `<index>`
 *  of a custom funder's synthetic identity. */
type TaggedFunder = FunderRole & { _flowType: PaymentFlowType };
type TaggedCustomFunder = CustomFunderRole & { _flowType: PaymentFlowType };

const isCustomFunder = (funder: TaggedFunder): funder is TaggedCustomFunder => typeof funder.getBalances === "function";

type FunderEntry = {
    address: Address | undefined;
    funder: TaggedFunder;
    walletName: string | undefined;
    walletType?: WalletVm;
    flowType: PaymentFlowType;
    hasFee?: boolean | undefined;
};

export const validateParams = (params: PaymentsWidgetSDKParams): PaymentsWidgetSDKParams => {
    try {
        return PaymentsWidgetSDKParams.parse(params) as PaymentsWidgetSDKParams;
    } catch (error) {
        logError(error);
        throw error;
    }
};

export const validateOverrides = (overridesArg: unknown): SDKOverrides => {
    try {
        return SDKOverrides.parse(overridesArg);
    } catch (error) {
        logError(error);
        throw error;
    }
};

export const computeWindowOrigin = (overrides: SDKOverrides): string => {
    return (overrides.dangerouslyOverrideHallidayDomainName || HALLIDAY_DOMAIN).replace(/\/$/, "");
};

export const remapOverrides = (overrides: SDKOverrides) => ({
    apiBaseUrl: overrides.dangerouslyOverrideApiBaseUrl,
    ipAddress: overrides.dangerouslyOverrideIPAddress,
    featureFlags: overrides.dangerouslyOverrideFeatureFlags,
    onramps: overrides.dangerouslyOverrideOnramps,
    offramps: overrides.dangerouslyOverrideOfframps,
    flags: overrides.dangerouslyOverrideFlags,
    hops: overrides.dangerouslyOverrideHops,
    demoScenario: overrides.dangerouslyOverrideDemoScenario,
});

export type ResolvedWalletMeta = {
    ownerMeta?: OwnerMeta | undefined;
    funderRegistry: FunderRegistry;
    funderMeta: WalletMeta[];
};

export const resolveWalletMeta = async (params: PaymentsWidgetSDKParams): Promise<ResolvedWalletMeta> => {
    const { owner } = params;

    // Only the `direct` arm is a real host wallet (getAddress + signers). wallet-auth/otp-auth owners
    // carry serializable data only, so they never resolve an address via getAddress nor act as a funder.
    const directOwner = owner && (!owner.type || owner.type === "direct") ? owner : undefined;

    // Tagged funder entries: (funderRole + assigned flowType)
    const taggedFunders: TaggedFunder[] = [];

    // funder/funders/owner → "wallet"
    if (params.funder) taggedFunders.push({ ...params.funder, _flowType: "wallet" });
    if (params.funders) params.funders.forEach((f) => taggedFunders.push({ ...f, _flowType: "wallet" }));
    if (directOwner && typeof directOwner.sendTransaction === "function") {
        taggedFunders.push({
            getAddress: directOwner.getAddress,
            sendTransaction: directOwner.sendTransaction,
            // Carried from the owner rather than hardcoded. No cast needed: `OwnerRole` and
            // `EvmFunderRole` narrow the SAME inherited `walletType` field to the same `"EVM"` literal,
            // so an owner passes through as a funder directly. Spread conditionally (the idiom already
            // used for `walletName` below) because `exactOptionalPropertyTypes` rejects a
            // present-but-undefined optional property; an absent tag then takes the shared `?? "EVM"`.
            ...(directOwner.walletType !== undefined && { walletType: directOwner.walletType }),
            _flowType: "wallet",
        });
    }

    // withdrawFunder → "withdraw"
    if (params.withdrawFunder) {
        taggedFunders.push({ ...params.withdrawFunder, _flowType: "withdraw" });
    }

    // Per-arm owner address source: direct resolves via getAddress; wallet-auth/otp-auth use the
    // config-supplied address (a wallet address, or an email for otp-auth); no owner → undefined.
    const ownerAddressPromise: Promise<Address | undefined> = directOwner
        ? directOwner.getAddress().catch(() => undefined)
        : owner && (owner.type === "wallet-auth" || owner.type === "otp-auth")
          ? Promise.resolve(owner.address)
          : Promise.resolve(undefined);

    const [ownerAddress, ...funderEntries] = await Promise.all([
        ownerAddressPromise,
        ...taggedFunders.map(async (f, index): Promise<FunderEntry> => {
            if (isCustomFunder(f)) {
                return {
                    address: `${CUSTOM_FUNDER_ADDRESS_PREFIX}${f._flowType}:${index}`,
                    funder: f,
                    walletName: f.walletName,
                    flowType: f._flowType,
                    hasFee: typeof f.getFee === "function" || undefined,
                };
            }
            return {
                address: await f.getAddress().catch(() => undefined),
                funder: f,
                walletName: f.walletName,
                walletType: f.walletType ?? "EVM",
                flowType: f._flowType,
            };
        }),
    ]);

    // Build ownerMeta per-arm. direct: legacy shape { address, walletName? } with NO `type` (keeps
    // direct serialization byte-for-byte and old widgets happy), only when the address resolved.
    // wallet-auth/otp-auth: explicit `type`, taken straight from config.
    let ownerMeta: OwnerMeta | undefined;
    if (directOwner) {
        ownerMeta = ownerAddress
            ? {
                  address: ownerAddress,
                  ...(directOwner.walletName !== undefined && { walletName: directOwner.walletName }),
              }
            : undefined;
    } else if (owner?.type === "wallet-auth") {
        ownerMeta = { type: "wallet-auth", walletType: owner.walletType, address: owner.address };
    } else if (owner?.type === "otp-auth") {
        ownerMeta = { type: "otp-auth", address: owner.address };
    }

    // Dedup by (address, flowType) composite key — skip entries where getAddress failed
    const funderRegistry: FunderRegistry = new Map();
    const funderMeta: WalletMeta[] = [];
    const seenKeys = new Set<string>();

    for (const entry of funderEntries) {
        if (!entry.address) continue;
        const addrLower = entry.address.toLowerCase();
        const key = `${addrLower}|${entry.flowType}`;
        if (seenKeys.has(key)) continue;
        seenKeys.add(key);
        if (!funderRegistry.has(addrLower)) {
            const { funder } = entry;
            if (isCustomFunder(funder)) {
                funderRegistry.set(addrLower, {
                    walletType: "CUSTOM",
                    getBalances: funder.getBalances,
                    sendFunds: funder.sendFunds,
                    getFee: funder.getFee,
                });
            } else {
                funderRegistry.set(
                    addrLower,
                    funder.walletType === "SOL"
                        ? { walletType: "SOL", sendTransaction: funder.sendTransaction }
                        : { walletType: "EVM", sendTransaction: funder.sendTransaction },
                );
            }
        }
        funderMeta.push({
            address: entry.address,
            walletName: entry.walletName,
            flowType: entry.flowType,
            walletType: entry.walletType,
            hasFee: entry.hasFee,
        });
    }

    return { ownerMeta, funderRegistry, funderMeta };
};

export const computeSDKAdditions = (
    params: PaymentsWidgetSDKParams,
    windowType: WindowType,
    _overrides: SDKOverrides,
    resolved: { ownerMeta?: OwnerMeta | undefined; funderMeta: WalletMeta[] },
): SDKAdditions => {
    const { owner, onConnectWallet, generateFeeSponsorshipGrant } = params;
    // signTypedData exists only on the direct arm; narrow so hasOwner stays direct-only (the new auth
    // arms have no signTypedData ⇒ hasOwner is false for them without changing the hasOwner check).
    const signTypedData: SignTypedData | undefined =
        owner && (!owner.type || owner.type === "direct") ? owner.signTypedData : undefined;
    const { ownerMeta, funderMeta } = resolved;

    // TODO: remove hasOwner/hasTxHandler once all widget versions read from ownerMeta/funderMeta
    const hasOwner = !!ownerMeta && Address.safeParse(ownerMeta.address).success && typeof signTypedData === "function";
    const hasTxHandler = funderMeta.length > 0;
    const hasConnect = typeof onConnectWallet === "function";
    const hasFeeSponsorship = typeof generateFeeSponsorshipGrant === "function";

    let hostOrigin: string | null = null;
    let hostUrl: string | null = null;
    let appMode: AppMode = "FULL";
    try {
        if (windowType === "MODAL") {
            appMode = isMobile() ? "OVERLAY" : "MODAL";
        } else if (windowType === "EMBED") {
            appMode = "EMBED";
        }
        hostOrigin = window.origin;
        hostUrl = window.location.href;
    } catch {
        // OK if window.origin / location access denied
    }

    return {
        hasOwner,
        hasTxHandler,
        hasConnect,
        hasFeeSponsorship,
        appMode,
        ownerMeta,
        funderMeta,
        hostOrigin,
        hostUrl,
        sdkVersion: SDK_VERSION,
    };
};
